import { FiCpu, FiClock, FiDollarSign, FiExternalLink, FiSquare, FiActivity } from 'react-icons/fi';

interface Session {
  id: string;
  notebook_id: string;
  instance_id: string;
  instance_type: string;
  provider: string;
  region: string;
  proxy_status: string;
  created_at: string | null;
  last_accessed: string | null;
  cost_per_hour: number;
  jupyter_url: string | null;
}

interface SessionCardProps {
  session: Session;
  onStop: (sessionId: string) => void;
  onOpen: (jupyterUrl: string) => void;
  stopping?: boolean;
}

export default function SessionCard({ session, onStop, onOpen, stopping }: SessionCardProps) {
  const getUptime = () => {
    if (!session.created_at) return 'Unknown';
    const start = new Date(session.created_at);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `${diffMins}m`;
    }
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    if (diffHours < 24) {
      return `${diffHours}h ${remainingMins}m`;
    }
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ${diffHours % 24}h`;
  };

  const getCurrentCost = () => {
    if (!session.cost_per_hour) return '\u2014';
    if (!session.created_at) return '\u2014';
    const start = new Date(session.created_at);
    const now = new Date();
    const hours = (now.getTime() - start.getTime()) / 3600000;
    return `$${(hours * session.cost_per_hour).toFixed(2)}`;
  };

  const getStatusColor = () => {
    switch (session.proxy_status) {
      case 'active':
        return 'text-green-400 bg-green-900/30';
      case 'starting':
      case 'pending':
        return 'text-yellow-400 bg-yellow-900/30';
      default:
        return 'text-gray-400 bg-gray-900/30';
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md p-5 hover:border-gray-700 transition-colors">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-800 rounded-md flex items-center justify-center">
            <FiCpu className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-white">{session.instance_type}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor()}`}>
                {session.proxy_status}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {session.provider.toUpperCase()} • {session.region}
            </p>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-gray-800/50 rounded-md p-3">
          <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
            <FiClock className="w-3 h-3" />
            Uptime
          </div>
          <p className="text-white font-medium">{getUptime()}</p>
        </div>
        <div className="bg-gray-800/50 rounded-md p-3">
          <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
            <FiDollarSign className="w-3 h-3" />
            Cost
          </div>
          <p className="text-white font-medium">{getCurrentCost()}</p>
        </div>
        <div className="bg-gray-800/50 rounded-md p-3">
          <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
            <FiActivity className="w-3 h-3" />
            Rate
          </div>
          <p className="text-white font-medium">${session.cost_per_hour.toFixed(2)}/hr</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {session.jupyter_url && (
          <button
            onClick={() => onOpen(session.jupyter_url!)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors text-sm"
          >
            <FiExternalLink className="w-4 h-4" />
            Open Notebook
          </button>
        )}
        <button
          onClick={() => onStop(session.id)}
          disabled={stopping}
          className={`flex items-center justify-center gap-2 px-3 py-2 font-medium rounded-md transition-colors text-sm ${
            session.jupyter_url ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'flex-1 bg-red-900/30 text-red-400 hover:bg-red-900/50'
          } ${stopping ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <FiSquare className="w-4 h-4" />
          {stopping ? 'Stopping...' : 'Stop'}
        </button>
      </div>
    </div>
  );
}
